import React, { useEffect, useMemo, useState } from "react";
import api from "../api";
import { toast } from "sonner";
import {
  Receipt, Plus, PencilSimple, Trash, CircleNotch, X, CheckCircle,
  MagnifyingGlass, ArrowsClockwise, CurrencyDollar, Scales,
} from "@phosphor-icons/react";

const money = (n) =>
  n == null ? "—" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const tonsFmt = (n) =>
  n == null ? "—" : `${Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })} T`;
const todayISO = () => new Date().toISOString().slice(0, 10);
const fmtDate = (s) => {
  if (!s) return "—";
  try { return new Date(`${s}T00:00:00`).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return s; }
};

const inputCls = "w-full px-3 py-2 border border-[#012340]/20 rounded-sm text-sm text-[#012340] focus:outline-none focus:border-[#CFAB7A]";

function SummaryCard({ label, main, sub, Icon, testid }) {
  return (
    <div className="bg-white border border-[#012340]/10 rounded-sm p-4" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-bold uppercase tracking-[0.28em] text-[#5C738A]">{label}</div>
        {Icon && <Icon size={16} weight="bold" className="text-[#CFAB7A]" />}
      </div>
      <div className="mt-2 font-heading font-black text-2xl text-[#012340]">{main}</div>
      {sub && <div className="text-xs text-[#5C738A] mt-1">{sub}</div>}
    </div>
  );
}

export default function DisposalTickets() {
  const [tickets, setTickets] = useState([]);
  const [summary, setSummary] = useState(null);
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState({ date_from: "", date_to: "", facility: "", q: "" });

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    dump_date: todayISO(),
    tons: "",
    price_per_ton: "",
    total_cost: "",
    facility: "",
    ticket_number: "",
    route_id: "",
    notes: "",
    _overrideTotal: false,
  });

  async function reload() {
    setLoading(true);
    try {
      const params = {};
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (filters.facility) params.facility = filters.facility;
      const [t, s, r] = await Promise.all([
        api.get("/disposal-tickets", { params }),
        api.get("/disposal-tickets/summary"),
        api.get("/routes").catch(() => ({ data: [] })),
      ]);
      setTickets(t.data.tickets || []);
      setSummary(s.data);
      setRoutes(Array.isArray(r.data) ? r.data : (r.data?.routes || []));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load disposal tickets");
    } finally { setLoading(false); }
  }

  useEffect(() => { reload(); }, []);

  const routeLabel = useMemo(() => {
    const m = new Map();
    routes.forEach((r) => m.set(r.id, `#${r.route_number}${r.name ? ` · ${r.name}` : ""}`));
    return m;
  }, [routes]);

  const filteredTickets = useMemo(() => {
    const q = filters.q.trim().toLowerCase();
    if (!q) return tickets;
    return tickets.filter((t) => {
      const bag = [t.facility, t.ticket_number, t.notes, t.dump_date, routeLabel.get(t.route_id) || ""].join(" ").toLowerCase();
      return bag.includes(q);
    });
  }, [tickets, filters.q, routeLabel]);

  const autoTotal = useMemo(() => {
    const t = parseFloat(form.tons); const p = parseFloat(form.price_per_ton);
    if (isFinite(t) && isFinite(p)) return (t * p).toFixed(2);
    return "";
  }, [form.tons, form.price_per_ton]);

  function openNew() {
    setEditing(null);
    setForm({
      dump_date: todayISO(), tons: "", price_per_ton: "", total_cost: "",
      facility: "", ticket_number: "", route_id: "", notes: "", _overrideTotal: false,
    });
    setShowForm(true);
  }
  function openEdit(t) {
    setEditing(t);
    setForm({
      dump_date: t.dump_date,
      tons: String(t.tons ?? ""),
      price_per_ton: String(t.price_per_ton ?? ""),
      total_cost: String(t.total_cost ?? ""),
      facility: t.facility || "",
      ticket_number: t.ticket_number || "",
      route_id: t.route_id || "",
      notes: t.notes || "",
      _overrideTotal: !!t.total_cost_overridden,
    });
    setShowForm(true);
  }

  async function submit(e) {
    e.preventDefault();
    if (!form.dump_date) { toast.error("Dump date is required"); return; }
    if (!form.tons || parseFloat(form.tons) <= 0) { toast.error("Tons must be > 0"); return; }
    if (form.price_per_ton === "") { toast.error("Price / ton is required"); return; }
    setSaving(true);
    try {
      const payload = {
        dump_date: form.dump_date,
        tons: parseFloat(form.tons),
        price_per_ton: parseFloat(form.price_per_ton),
        facility: form.facility || null,
        ticket_number: form.ticket_number || null,
        route_id: form.route_id || null,
        notes: form.notes || null,
      };
      if (form._overrideTotal && form.total_cost !== "") {
        payload.total_cost = parseFloat(form.total_cost);
      } else if (!form._overrideTotal && editing?.total_cost_overridden) {
        payload.total_cost = null;
      }
      if (editing) {
        await api.patch(`/disposal-tickets/${editing.id}`, payload);
        toast.success("Ticket updated");
      } else {
        await api.post("/disposal-tickets", payload);
        toast.success("Ticket saved");
      }
      setShowForm(false);
      await reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  }

  async function remove(t) {
    if (!window.confirm(`Delete disposal ticket from ${t.dump_date}?`)) return;
    try {
      await api.delete(`/disposal-tickets/${t.id}`);
      toast.success("Ticket deleted");
      await reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  }

  return (
    <div className="space-y-6" data-testid="disposal-tickets-page">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading font-black text-2xl text-[#012340]">Disposal Tickets</h1>
          <p className="text-sm text-[#5C738A] mt-1">Track real landfill/facility tickets — feeds daily, weekly, and monthly disposal actuals.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={reload}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-sm border border-[#012340]/20 text-xs font-bold uppercase tracking-[0.2em] text-[#012340] hover:bg-[#012340]/5"
            data-testid="dt-refresh"
          >
            <ArrowsClockwise size={12} weight="bold" /> Refresh
          </button>
          <button
            onClick={openNew}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-sm bg-[#012340] text-white text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#012340]/90"
            data-testid="dt-add"
          >
            <Plus size={12} weight="bold" /> New Ticket
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <SummaryCard
          testid="dt-sum-today"
          label="Today"
          Icon={CurrencyDollar}
          main={money(summary?.today?.total_cost)}
          sub={`${tonsFmt(summary?.today?.tons)} · ${summary?.today?.count || 0} tickets`}
        />
        <SummaryCard
          testid="dt-sum-week"
          label="This Week"
          Icon={Scales}
          main={money(summary?.this_week?.total_cost)}
          sub={`${tonsFmt(summary?.this_week?.tons)} · avg ${money(summary?.this_week?.avg_cost_per_ton)}/T`}
        />
        <SummaryCard
          testid="dt-sum-month"
          label="This Month"
          Icon={Receipt}
          main={money(summary?.this_month?.total_cost)}
          sub={`${tonsFmt(summary?.this_month?.tons)} · avg ${money(summary?.this_month?.avg_cost_per_ton)}/T`}
        />
      </div>

      {/* Filters */}
      <div className="bg-white border border-[#012340]/10 rounded-sm p-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5C738A]">From</label>
            <input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} className={inputCls} data-testid="dt-filter-from" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5C738A]">To</label>
            <input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} className={inputCls} data-testid="dt-filter-to" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5C738A]">Facility</label>
            <input type="text" placeholder="e.g. Waste Mgmt" value={filters.facility} onChange={(e) => setFilters({ ...filters, facility: e.target.value })} className={inputCls} data-testid="dt-filter-facility" />
          </div>
          <div className="md:col-span-1">
            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5C738A]">Search</label>
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#5C738A]" />
              <input
                type="text" placeholder="Ticket #, notes…"
                value={filters.q}
                onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                className={`${inputCls} pl-7`}
                data-testid="dt-filter-q"
              />
            </div>
          </div>
          <div className="flex items-end gap-2">
            <button
              onClick={reload}
              className="flex-1 px-3 py-2 rounded-sm bg-[#012340] text-white text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#012340]/90"
              data-testid="dt-apply"
            >
              Apply
            </button>
            <button
              onClick={() => { setFilters({ date_from: "", date_to: "", facility: "", q: "" }); setTimeout(reload, 0); }}
              className="px-3 py-2 rounded-sm border border-[#012340]/20 text-xs font-bold uppercase tracking-[0.2em] text-[#012340] hover:bg-[#012340]/5"
              data-testid="dt-clear"
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-[#012340]/10 rounded-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-[#012340]/10 flex items-center justify-between">
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#5C738A]">
            Tickets ({filteredTickets.length}{filters.q && tickets.length !== filteredTickets.length ? ` of ${tickets.length}` : ""})
          </div>
        </div>
        {loading ? (
          <div className="p-8 text-center text-sm text-[#5C738A]"><CircleNotch size={16} className="animate-spin inline mr-2" /> Loading…</div>
        ) : filteredTickets.length === 0 ? (
          <div className="p-8 text-center text-sm text-[#5C738A]">No tickets found. Click <span className="font-bold text-[#012340]">New Ticket</span> to log a dump.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F5F5F5] text-[10px] uppercase tracking-[0.18em] text-[#5C738A]">
                <tr>
                  <th className="px-4 py-2 text-left font-bold">Date</th>
                  <th className="px-4 py-2 text-left font-bold">Facility</th>
                  <th className="px-4 py-2 text-left font-bold">Ticket #</th>
                  <th className="px-4 py-2 text-right font-bold">Tons</th>
                  <th className="px-4 py-2 text-right font-bold">Price / Ton</th>
                  <th className="px-4 py-2 text-right font-bold">Total Cost</th>
                  <th className="px-4 py-2 text-left font-bold">Route</th>
                  <th className="px-4 py-2 text-right font-bold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTickets.map((t) => (
                  <tr key={t.id} className="border-t border-[#012340]/5 hover:bg-[#012340]/[.02]" data-testid={`dt-row-${t.id}`}>
                    <td className="px-4 py-2 text-[#012340] whitespace-nowrap">{fmtDate(t.dump_date)}</td>
                    <td className="px-4 py-2 text-[#012340]">{t.facility || "—"}</td>
                    <td className="px-4 py-2 text-[#012340] font-mono text-xs">{t.ticket_number || "—"}</td>
                    <td className="px-4 py-2 text-right text-[#012340]">{tonsFmt(t.tons)}</td>
                    <td className="px-4 py-2 text-right text-[#012340]">{money(t.price_per_ton)}</td>
                    <td className="px-4 py-2 text-right text-[#012340] font-bold">
                      {money(t.total_cost)}
                      {t.total_cost_overridden && (
                        <span className="ml-1 text-[9px] font-bold text-[#CFAB7A] uppercase tracking-[0.15em]">Manual</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-[#5C738A]">{routeLabel.get(t.route_id) || "—"}</td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      <button
                        onClick={() => openEdit(t)}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-[#012340]/15 text-xs text-[#012340] hover:bg-[#012340]/5 mr-1"
                        data-testid={`dt-edit-${t.id}`}
                      >
                        <PencilSimple size={12} weight="bold" /> Edit
                      </button>
                      <button
                        onClick={() => remove(t)}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-[#B71C1C]/30 text-xs text-[#B71C1C] hover:bg-[#B71C1C]/5"
                        data-testid={`dt-del-${t.id}`}
                      >
                        <Trash size={12} weight="bold" /> Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Form modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" onClick={() => setShowForm(false)} data-testid="dt-form-modal">
          <form
            onSubmit={submit}
            onClick={(e) => e.stopPropagation()}
            className="bg-white text-[#012340] rounded-sm w-full max-w-lg p-5 space-y-3 max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-1">
              <h2 className="font-heading font-black text-lg">
                {editing ? "Edit Disposal Ticket" : "New Disposal Ticket"}
              </h2>
              <button type="button" onClick={() => setShowForm(false)} className="text-[#5C738A] hover:text-[#012340]"><X size={16} weight="bold" /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="col-span-2">
                <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#5C738A] mb-1">Dump Date *</div>
                <input type="date" required value={form.dump_date} onChange={(e) => setForm((f) => ({ ...f, dump_date: e.target.value }))} className={inputCls} data-testid="dt-form-date" />
              </label>
              <label>
                <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#5C738A] mb-1">Tons *</div>
                <input type="number" step="0.01" min="0" required value={form.tons} onChange={(e) => setForm((f) => ({ ...f, tons: e.target.value }))} className={inputCls} data-testid="dt-form-tons" />
              </label>
              <label>
                <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#5C738A] mb-1">Price / Ton *</div>
                <input type="number" step="0.01" min="0" required value={form.price_per_ton} onChange={(e) => setForm((f) => ({ ...f, price_per_ton: e.target.value }))} className={inputCls} data-testid="dt-form-ppt" />
              </label>
              <label className="col-span-2">
                <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#5C738A] mb-1 flex items-center gap-2">
                  Total Cost <span className="font-normal normal-case tracking-normal text-[#5C738A]">(auto: {money(autoTotal || 0)})</span>
                  <label className="ml-auto text-[10px] font-normal normal-case tracking-normal cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form._overrideTotal}
                      onChange={(e) => setForm((f) => ({ ...f, _overrideTotal: e.target.checked, total_cost: e.target.checked ? (f.total_cost || autoTotal) : "" }))}
                      className="mr-1"
                      data-testid="dt-form-override"
                    />
                    Manual override
                  </label>
                </div>
                <input
                  type="number" step="0.01" min="0"
                  disabled={!form._overrideTotal}
                  value={form._overrideTotal ? form.total_cost : autoTotal}
                  onChange={(e) => setForm((f) => ({ ...f, total_cost: e.target.value }))}
                  className={`${inputCls} ${!form._overrideTotal ? "bg-[#F5F5F5] text-[#5C738A]" : ""}`}
                  data-testid="dt-form-total"
                />
              </label>
              <label>
                <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#5C738A] mb-1">Facility</div>
                <input type="text" value={form.facility} onChange={(e) => setForm((f) => ({ ...f, facility: e.target.value }))} placeholder="Waste Mgmt Landfill" className={inputCls} data-testid="dt-form-facility" />
              </label>
              <label>
                <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#5C738A] mb-1">Ticket #</div>
                <input type="text" value={form.ticket_number} onChange={(e) => setForm((f) => ({ ...f, ticket_number: e.target.value }))} className={inputCls} data-testid="dt-form-ticketno" />
              </label>
              <label className="col-span-2">
                <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#5C738A] mb-1">Linked Route</div>
                <select
                  value={form.route_id}
                  onChange={(e) => setForm((f) => ({ ...f, route_id: e.target.value }))}
                  className={inputCls}
                  data-testid="dt-form-route"
                >
                  <option value="">— None —</option>
                  {routes.map((r) => (
                    <option key={r.id} value={r.id}>#{r.route_number}{r.name ? ` · ${r.name}` : ""}</option>
                  ))}
                </select>
              </label>
              <label className="col-span-2">
                <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#5C738A] mb-1">Notes</div>
                <textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} rows={2} className={inputCls} data-testid="dt-form-notes" />
              </label>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 rounded-sm border border-[#012340]/20 text-xs font-bold uppercase tracking-[0.2em]">Cancel</button>
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-sm bg-[#012340] text-white text-xs font-bold uppercase tracking-[0.2em] disabled:opacity-40"
                data-testid="dt-form-submit"
              >
                {saving ? <CircleNotch className="animate-spin" size={12} /> : <CheckCircle size={12} weight="bold" />}
                {editing ? "Save Changes" : "Save Ticket"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
